import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Footprints, Beaker, BookOpen, CheckCircle2, FlaskConical, Dumbbell, Sun, Wheat } from 'lucide-react';
import { getProgress, PROGRESS_KEY } from '../../pages/GuidesHubPage';

const GUIDES = [
  {
    slug: 'the-ammortal-chamber-a-deep-dive-into-the-future-of-wellness',
    title: 'The Ammortal Chamber',
    subtitle: '10 Therapies in 30 Minutes',
    category: 'Biohacking',
    readTime: 14,
    icon: Zap,
    color: '#f5a623',
    hook: 'Stack PEMF, red light, hydrogen therapy & 7 more modalities in one session.',
  },
  {
    slug: 'the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing',
    title: 'The Wolverine Complex',
    subtitle: 'BPC-157 + TB-500 Stack',
    category: 'Peptides',
    readTime: 12,
    icon: Shield,
    color: '#22c55e',
    hook: 'The synergistic peptide stack for tissue repair and accelerated recovery.',
  },
  {
    slug: 'the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready',
    title: 'HYROX Training Guide',
    subtitle: '8 Weeks to Race-Ready',
    category: 'HYROX',
    readTime: 15,
    icon: Footprints,
    color: '#c41e3a',
    hook: 'Station breakdowns, weekly plans, pacing strategy & race-day nutrition.',
  },
  {
    slug: 'peptide-side-effects-what-the-research-actually-says',
    title: 'Peptide Side Effects',
    subtitle: 'What the Research Says',
    category: 'Peptides',
    readTime: 18,
    icon: Beaker,
    color: '#8b5cf6',
    hook: 'Interactive comparison tool and decision helper backed by clinical data.',
  },
  {
    slug: 'retatrutide-cancer-prevention-science',
    title: 'Retatrutide & Cancer',
    subtitle: 'Triple-Agonist Tumor Science',
    category: 'Peptides',
    readTime: 22,
    icon: FlaskConical,
    color: '#E63946',
    hook: '14x tumor reduction vs. semaglutide. The triple-agonist cancer prevention data.',
  },
  {
    slug: 'how-to-train-like-an-athlete',
    title: 'Train Like an Athlete',
    subtitle: 'Complete Training Guide',
    category: 'Fitness',
    readTime: 10,
    icon: Dumbbell,
    color: '#3b82f6',
    hook: 'Structured periodization, interactive weekly plan, and athlete nutrition.',
  },
  {
    slug: 'hyrox-vs-crossfit-comparison',
    title: 'HYROX vs CrossFit',
    subtitle: 'Interactive Comparison',
    category: 'HYROX',
    readTime: 11,
    icon: Footprints,
    color: '#c41e3a',
    hook: 'Format face-off, cost breakdown, and a 5-question quiz to find your match.',
  },
  {
    slug: 'high-protein-meal-prep-guide',
    title: 'High Protein Meal Prep',
    subtitle: '4 Recipes + Grocery List',
    category: 'Nutrition',
    readTime: 8,
    icon: Sun,
    color: '#22c55e',
    hook: '126g+ protein daily with 4 recipes, a weekly planner, and interactive grocery checklist.',
  },
  {
    slug: 'new-food-pyramid-2026-dietary-guidelines',
    title: 'The New Food Pyramid 2026',
    subtitle: 'RFK\'s Real Food Reset',
    category: 'Nutrition',
    readTime: 16,
    icon: Wheat,
    color: '#f59e0b',
    hook: 'More protein, full-fat dairy, less processed food. The inverted pyramid explained with interactive comparison.',
  },
];

export default function RelatedGuides({ currentSlug }) {
  const [progress, setProgress] = useState({});
  const related = GUIDES.filter(g => g.slug !== currentSlug).slice(0, 3);

  useEffect(() => {
    setProgress(getProgress());
    const onStorage = (e) => {
      if (e.key === PROGRESS_KEY) setProgress(getProgress());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <section className="mt-16 mb-12" data-testid="related-guides-section">
      <div className="flex items-center gap-3 mb-6">
        <BookOpen className="w-5 h-5 text-zinc-400" />
        <h3 className="font-heading text-xl text-white tracking-wide uppercase">Continue Reading</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {related.map((guide) => {
          const Icon = guide.icon;
          const pct = Math.round(progress[guide.slug] || 0);
          const isComplete = pct >= 95;
          const isStarted = pct > 0;
          const remainingMin = isStarted ? Math.max(1, Math.round(guide.readTime * (1 - pct / 100))) : guide.readTime;

          return (
            <Link
              key={guide.slug}
              to={`/blogs/article/${guide.slug}`}
              className="group relative block border border-zinc-800 bg-zinc-900/60 hover:border-zinc-600 transition-all duration-300 overflow-hidden"
              data-testid={`related-guide-${guide.category.toLowerCase()}`}
            >
              {/* Progress bar at top of card */}
              {isStarted && (
                <div className="h-[3px] bg-zinc-800" data-testid={`progress-bar-${guide.slug.slice(0, 20)}`}>
                  <div
                    className="h-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: guide.color }}
                  />
                </div>
              )}

              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="w-7 h-7 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${guide.color}18`, color: guide.color }}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {guide.category}
                  </span>

                  {/* Progress badge */}
                  {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                  {isComplete ? (
                    <span
                      className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${guide.color}20`, color: guide.color }}
                      data-testid={`badge-complete-${guide.category.toLowerCase()}`}
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Done
                    </span>
                  ) : isStarted ? (
                    <span
                      className="ml-auto text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: `${guide.color}15`, color: guide.color }}
                      data-testid={`badge-progress-${guide.category.toLowerCase()}`}
                    >
                      {pct}%
                    </span>
                  ) : (
                    <span className="ml-auto text-[10px] text-zinc-600 font-mono">
                      {guide.readTime} min
                    </span>
                  )}
                </div>

                <h4 className="text-sm font-bold text-white group-hover:text-zinc-200 transition-colors mb-1 leading-tight">
                  {guide.title}
                </h4>
                <p className="text-[11px] text-zinc-500 leading-relaxed mb-3">
                  {guide.hook}
                </p>

                <div className="flex items-center justify-between">
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider transition-colors"
                    style={{ color: guide.color }}
                  >
                    {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                    {isComplete ? 'Read Again' : isStarted ? 'Continue' : 'Read Guide'}
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </span>

                  {isStarted && !isComplete && (
                    <span className="text-[10px] text-zinc-600 font-mono">
                      ~{remainingMin} min left
                    </span>
                  )}
                </div>
              </div>

              <div className="h-[2px] w-0 group-hover:w-full transition-all duration-500" style={{ backgroundColor: guide.color }} />
            </Link>
          );
        })}
      </div>
      <div className="text-center mt-6">
        <Link
          to="/guides"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
          data-testid="related-guides-hub-link"
        >
          <BookOpen className="w-3.5 h-3.5" />
          View All Guides
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </section>
  );
}
